import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Col, DatePicker, Row, Typography,
} from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import moment from 'moment';

import FormBuilder from '../FormBuilder/FormBuilder';
import FormSignature from './FormSignature';
import FormSubmitChildButton from '../FormSubmitChildButton';
import FormApprovalButton, {
  APPROVE_FORM_SUBMISSION, REJECT_FORM_SUBMISSION,
} from '../FormApprovalButton';

import OnTraccrButton from '../../common/buttons/OnTraccrButton';

import Permissions from '../../auth/Permissions';
import { getIdMap } from '../../helpers/helpers';
import useToggle from '../../common/hooks/useToggle';
import SnapshotDrawer from '../../common/customFields/SnapshotDrawer';
import { findSnapshotsForRange, getRelevantSnapshotData } from '../formHelpers';
import OnTraccrEmpty from '../../common/OnTraccrEmpty';
import { EDIT_TYPES, generateResponseMap } from '../ResponderHelpers';
import DuplicateCardsSlider from '../DuplicateCardsSlider';
import useDuplicateCardDetection from '../hooks/useDuplicateCardDetection';
import { getFormSnapshotById } from '../state/forms.actions';

export default function FormDetailFields({
  formId,
  formData,
  employeeSignature,
  signatureMetadata,
  signatureConfig,
  allowResubmission,
  allowEdit,
  onEditClicked,
  allowSubmitChild,
  onSubmitChildClicked,
  childSubmitMode,
  setChildSubmitMode,
  sliderVisible,
  setSelectedFile,
  setSelectedFileDetails,
  isClosed,
  divisionId,
  approvals = [],
  formState,
  shouldGetTemplates,
  sectionPermissionMap,
  onCardColumnClick,
  templateSchema = {},
}) {
  const dispatch = useDispatch();
  const formApprovals = useSelector((state) => state.forms.approvalSummary);
  const childForms = useSelector((state) => state.forms.childForms);
  const formTemplates = useSelector((state) => state.forms.assignedFormTemplates);
  const formSnapshotMap = useSelector((state) => state.forms.formSnapshotMap);
  const users = useSelector((state) => state.users.users);
  const selectedForm = useSelector((state) => state.forms.selectedForm);

  const [range, setRange] = useState();
  const [loadingSnapshotRange, setLoadingSnapshotRange] = useState(false);
  const [isDuplicateSliderVisible, setIsDuplicateSliderVisible] = useState(false);
  const {
    isToggled,
    toggle,
  } = useToggle();

  const userMap = useMemo(() => getIdMap(users), [users]);
  const formTemplateMap = useMemo(() => getIdMap(formTemplates), [formTemplates]);
  const {
    snapshots = [],
  } = useMemo(() => formSnapshotMap[formId] || { snapshots: [] }, [formSnapshotMap, formId]);

  const needsApproval = formId in formApprovals
    && (!formApprovals[formId].editable || formApprovals[formId].editableApprovals);

  // Allow on either the form template 'Allow Edit' option or the approval step 'Editable' option
  const canEdit = (needsApproval && !!formApprovals[formId]?.editableApprovals) || (allowEdit && Permissions.has('FORMS_WRITE'));
  const canResubmit = allowResubmission && Permissions.has('FORMS_WRITE');

  const childFormOptions = useMemo(() => (
    childForms.reduce((acc, id) => {
      if (id in formTemplateMap) {
        acc.push({
          value: id,
          label: formTemplateMap[id].name,
        });
      }
      return acc;
    }, [])
  ), [childForms, formTemplateMap]);

  const showSubmitChild = allowSubmitChild && childFormOptions.length;

  const snapshotRange = useMemo(() => {
    if (snapshots.length > 1) {
      return [
        moment.unix(snapshots[snapshots.length - 1].timestamp / 1000).startOf('day'),
        moment.unix(snapshots[0].timestamp / 1000).endOf('day'),
      ];
    }
    return null;
  }, [snapshots]);

  const disabledDate = useCallback((current) => {
    if (!snapshotRange) return false;
    return current < snapshotRange[0] || current > snapshotRange[1];
  }, [snapshotRange]);

  const onRangeChange = useCallback(async (newRange) => {
    if (!newRange?.length) {
      setRange();
      return;
    }
    const {
      firstSnapshot,
      secondSnapshot,
    } = findSnapshotsForRange({
      snapshots,
      range: newRange,
    });
    const firstNeedsLoad = firstSnapshot && !firstSnapshot.dataLoaded;
    const secondNeedsLoad = secondSnapshot && !secondSnapshot.dataLoaded;
    if (firstNeedsLoad || secondNeedsLoad) setLoadingSnapshotRange(true);
    if (firstNeedsLoad) {
      await dispatch(getFormSnapshotById(formId, firstSnapshot.id));
    }
    if (secondNeedsLoad) {
      await dispatch(getFormSnapshotById(formId, secondSnapshot.id));
    }
    setRange(newRange);
    setLoadingSnapshotRange(false);
  }, [snapshots, dispatch, formId]);

  useEffect(() => {
    if (!sliderVisible) {
      setRange();
    }
  }, [snapshotRange, sliderVisible]);

  const relevantFormData = useMemo(() => (
    getRelevantSnapshotData({
      formData,
      range,
      snapshots,
      setSelectedFile,
      setSelectedFileDetails,
    })
  ), [formData, range, snapshots, setSelectedFile, setSelectedFileDetails]);

  const responsesForDetection = useMemo(() => {
    if (!relevantFormData) return {};

    return generateResponseMap(relevantFormData);
  }, [relevantFormData]);

  const {
    duplicateCount,
    hasDuplicateDetection,
  } = useDuplicateCardDetection({
    formData: templateSchema,
    responses: responsesForDetection,
    visible: sliderVisible,
  });

  const latestRejectionMessage = useMemo(() => {
    const sortedApprovals = [...approvals].sort((a, b) => b.timestamp - a.timestamp);
    const firstApproval = sortedApprovals?.[0];

    if (!firstApproval || firstApproval?.state !== 'rejected') return null;

    const { note, userId } = firstApproval;
    const rejecterName = userMap[userId]?.name ?? 'Unknown';

    let msg = `Form was rejected by ${rejecterName}`;

    if (note) msg += ` with note: ${note}`;

    return msg;
  }, [approvals, userMap]);

  const showRejection = latestRejectionMessage && formState !== 'submitted';

  const hasFooter = !childSubmitMode
    && !isClosed
    && (needsApproval || canResubmit || showSubmitChild || canEdit);

  const builderStyle = hasFooter
    ? {
      marginBottom: 50,
    }
    : {
      bottom: 0,
    };

  const onCardTitleClick = useCallback(() => {
    if (selectedForm?.cardId && onCardColumnClick) {
      onCardColumnClick(selectedForm);
    }
  }, [dispatch, selectedForm?.cardId]);

  return (
    <>
      <div style={{
        height: '100%',
        width: '100%',
      }}
      >
        <FormBuilder
          formId={formId}
          isDisplay
          initialSections={relevantFormData ?? []}
          style={builderStyle}
          signatureView={(
            <FormSignature
              employeeSignature={employeeSignature}
              signatureMetadata={signatureMetadata}
              signatureConfig={signatureConfig}
            />
          )}
          shouldHideCustomRenderingFields
          divisions={[divisionId]}
          isDetailView
          shouldGetTemplates={shouldGetTemplates}
          sectionPermissionMap={sectionPermissionMap}
        >
          {showRejection && (
            <Row
              style={{ padding: '20px 35px 0px 20px', color: 'red' }}
            >
              {latestRejectionMessage}
            </Row>
          )}
          {needsApproval && hasDuplicateDetection && duplicateCount > 0 && (
            <Row style={{ padding: '20px 35px 0px 20px' }}>
              <OnTraccrButton
                type="back"
                icon={<WarningOutlined />}
                title={`${duplicateCount} Conflict${duplicateCount !== 1 ? 's' : ''} Detected`}
                onClick={() => setIsDuplicateSliderVisible(true)}
              />
            </Row>
          )}
          {
            selectedForm?.cardTitle && onCardColumnClick && (
              <Row style={{ padding: '20px 35px 0px 20px' }}>
                <Typography.Link onClick={onCardTitleClick} style={{ cursor: 'pointer' }}>
                  Card: {selectedForm.cardTitle}
                </Typography.Link>
              </Row>
            )
          }
          {snapshots.length > 1 && (
            <Row
              justify="space-between"
              style={{
                padding: '20px 35px 10px 20px',
              }}
            >
              <Col>
                <DatePicker.RangePicker
                  allowClear={false}
                  style={{ width: 250 }}
                  format="MMM Do YY"
                  disabledDate={disabledDate}
                  disabled={loadingSnapshotRange}
                  minDate
                  value={range}
                  onChange={onRangeChange}
                  getPopupContainer={(node) => node.parentNode}
                />
              </Col>
              <OnTraccrButton
                title="View Snapshots"
                onClick={toggle}
              />
            </Row>
          )}
          {!relevantFormData && (
            <OnTraccrEmpty title="Form Fields" />
          )}
        </FormBuilder>
        {hasFooter
          ? (
            <div
              className="drawer-footer"
              style={{
                zIndex: 1000,
              }}
            >
              <Row justify="end" gutter={10}>
                {!!canResubmit && (
                  <OnTraccrButton
                    title="Resubmit"
                    onClick={onEditClicked(EDIT_TYPES.RESUBMIT)}
                    type="cancel"
                    style={{
                      marginRight: needsApproval ? 8 : 0,
                      margin: 2,
                    }}
                  />
                )}
                {!!canEdit && (
                  <OnTraccrButton
                    title="Edit"
                    onClick={onEditClicked(EDIT_TYPES.EDIT)}
                    type="cancel"
                    style={{
                      marginRight: needsApproval ? 8 : 0,
                      margin: 2,
                    }}
                  />
                )}
                {!!needsApproval && (
                  <FormApprovalButton
                    formId={formId}
                    type={REJECT_FORM_SUBMISSION}
                    isViewMode
                    shouldCalculateErrors
                    sections={formData}
                    sectionPermissionMap={sectionPermissionMap}
                    responses={relevantFormData}
                  />
                )}
                {!!needsApproval && (
                  <FormApprovalButton
                    formId={formId}
                    type={APPROVE_FORM_SUBMISSION}
                    isViewMode
                    shouldCalculateErrors
                    sections={formData}
                    sectionPermissionMap={sectionPermissionMap}
                    responses={relevantFormData}
                  />
                )}
                {!!showSubmitChild && (
                  <FormSubmitChildButton
                    childForms={childFormOptions}
                    onSubmitChildClicked={onSubmitChildClicked}
                    setChildSubmitMode={setChildSubmitMode}
                  />
                )}
              </Row>
            </div>
          ) : null}
      </div>
      <SnapshotDrawer
        id={formId}
        open={sliderVisible && isToggled}
        onClose={toggle}
        snapshots={snapshots}
        setSelectedFile={setSelectedFile}
        setSelectedFileDetails={setSelectedFileDetails}
        getSnapshotById={getFormSnapshotById}
      />
      <DuplicateCardsSlider
        visible={sliderVisible && isDuplicateSliderVisible}
        onClose={() => setIsDuplicateSliderVisible(false)}
      />
    </>
  );
}

/* eslint-disable react/forbid-prop-types */
FormDetailFields.propTypes = {
  formData: PropTypes.array.isRequired,
  formId: PropTypes.string.isRequired,
  employeeSignature: PropTypes.object,
  allowEdit: PropTypes.bool.isRequired,
  allowResubmission: PropTypes.bool.isRequired,
  onEditClicked: PropTypes.func.isRequired,
  allowSubmitChild: PropTypes.bool.isRequired,
  onSubmitChildClicked: PropTypes.func.isRequired,
  childSubmitMode: PropTypes.bool.isRequired,
  setChildSubmitMode: PropTypes.func.isRequired,
  sliderVisible: PropTypes.bool.isRequired,
  setSelectedFile: PropTypes.func.isRequired,
  setSelectedFileDetails: PropTypes.func.isRequired,
  isClosed: PropTypes.bool,
  divisionId: PropTypes.string.isRequired,
  approvals: PropTypes.array,
  formState: PropTypes.string.isRequired,
  shouldGetTemplates: PropTypes.bool,
  sectionPermissionMap: PropTypes.shape({}),
  templateSchema: PropTypes.object,
  signatureMetadata: PropTypes.object,
  signatureConfig: PropTypes.object,
  onCardColumnClick: PropTypes.func,
};

FormDetailFields.defaultProps = {
  employeeSignature: undefined,
  isClosed: false,
  approvals: [],
  shouldGetTemplates: true,
  sectionPermissionMap: {},
  templateSchema: {},
  signatureMetadata: null,
  signatureConfig: null,
  onCardColumnClick: null,
};
